import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { clubRadarService } from "../../services/clubRadarService";
import { computeStatusCounts, filterVisible } from "./clubRadarFilters";

// Club Radar state — the club's shared watchlist (P3). Mirrors useAgentRadar's
// shape (one selected list drives the entries fetch; membership ops are optimistic
// then counts refetch) but carries the club depth: two list buckets (my_lists +
// shared_with_club), a per-entry club status (R7), and a status/scout-request
// filter that is applied CLIENT-SIDE so the filter chips can show live per-status
// counts (watchlists are small). label + sort go to the server as query params.
export const useClubRadar = () => {
  const [myLists, setMyLists] = useState([]);
  const [sharedLists, setSharedLists] = useState([]);
  const [labels, setLabels] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedList, setSelectedList] = useState(null); // list id
  const [loading, setLoading] = useState(false);
  const [listsLoading, setListsLoading] = useState(true);
  const [error, setError] = useState(null);

  // filter = "all" | one of CLUB_STATUS_ORDER | "scout_requests"  (single chip row)
  const [filter, setFilter] = useState("all");
  const [filterLabel, setFilterLabel] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const abortRef = useRef(null);

  // All lists a player can be added to / moved / copied to (my lists first, then
  // shared lists the viewer can also work on).
  const allLists = useMemo(() => [...myLists, ...sharedLists], [myLists, sharedLists]);

  // ── Lists + labels ───────────────────────────────────────────────────────────
  const fetchLists = useCallback(async () => {
    const res = await clubRadarService.getLists();
    if (res.success) {
      const my = res.data.my_lists || [];
      const shared = res.data.shared_with_club || [];
      setMyLists(my);
      setSharedLists(shared);
      setSelectedList((cur) => {
        const flat = [...my, ...shared];
        if (cur && flat.some((l) => l.id === cur)) return cur;
        const def = my.find((l) => l.is_default) || my[0] || shared[0];
        return def ? def.id : null;
      });
    }
    return res;
  }, []);

  const fetchLabels = useCallback(async () => {
    const res = await clubRadarService.getLabels();
    if (res.success) setLabels(res.data);
    return res;
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setListsLoading(true);
      await Promise.all([fetchLists(), fetchLabels()]);
      if (alive) setListsLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [fetchLists, fetchLabels]);

  // ── Entries for the selected list (server sorts + label-filters) ──────────────
  const fetchEntries = useCallback(async () => {
    if (!selectedList) {
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const params = { list: selectedList, sort: sortBy };
    if (filterLabel !== "all") params.label = filterLabel;
    try {
      const res = await clubRadarService.getEntries(params, controller.signal);
      if (res.success) setEntries(res.data);
      else setError(res.error || "Failed to load your radar.");
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;
      setError("Failed to load your radar. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [selectedList, sortBy, filterLabel]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // ── Client-side status / scout-request filter + live chip counts ─────────────
  const statusCounts = useMemo(() => computeStatusCounts(entries), [entries]);
  const visibleEntries = useMemo(() => filterVisible(entries, filter), [entries, filter]);

  // ── List CRUD ────────────────────────────────────────────────────────────────
  const createList = useCallback(
    async ({ name, description } = {}) => {
      const res = await clubRadarService.createList({ name, description });
      if (res.success) {
        await fetchLists();
        if (res.data?.id) setSelectedList(res.data.id);
      }
      return res;
    },
    [fetchLists],
  );

  const updateList = useCallback(async (id, patch) => {
    const res = await clubRadarService.updateList(id, patch);
    if (res.success) {
      const apply = (l) => (l.id === id ? { ...l, ...patch, ...(res.data || {}) } : l);
      setMyLists((prev) => prev.map(apply));
      setSharedLists((prev) => prev.map(apply));
    }
    return res;
  }, []);

  const deleteList = useCallback(
    async (id) => {
      const res = await clubRadarService.deleteList(id);
      if (res.success) {
        setSelectedList((cur) => {
          if (cur !== id) return cur;
          const def = myLists.find((l) => l.is_default);
          return def ? def.id : myLists[0]?.id || null;
        });
        await fetchLists();
        await fetchEntries();
      }
      return res;
    },
    [myLists, fetchLists, fetchEntries],
  );

  const setSharing = useCallback(
    async (id, payload) => {
      const res = await clubRadarService.setSharing(id, payload);
      if (res.success) await fetchLists();
      return res;
    },
    [fetchLists],
  );

  // ── Membership ops (optimistic entries, then refetch counts) ─────────────────
  const removeEntry = useCallback(
    async (entryId) => {
      const prev = entries;
      setEntries((cur) => cur.filter((e) => e.id !== entryId));
      const res = await clubRadarService.removeEntry(entryId, selectedList);
      if (res.success) fetchLists();
      else setEntries(prev);
      return res;
    },
    [entries, fetchLists, selectedList],
  );

  const moveEntry = useCallback(
    async (entryId, targetListId) => {
      const prev = entries;
      setEntries((cur) => cur.filter((e) => e.id !== entryId));
      const res = await clubRadarService.moveEntry(entryId, targetListId, selectedList);
      if (res.success) fetchLists();
      else setEntries(prev);
      return res;
    },
    [entries, fetchLists, selectedList],
  );

  const copyEntry = useCallback(
    async (entryId, targetListId) => {
      const res = await clubRadarService.copyEntry(entryId, targetListId);
      if (res.success) fetchLists();
      return res;
    },
    [fetchLists],
  );

  // Set the club status (R7) — optimistic on the entries array.
  const setStatus = useCallback(
    async (entryId, status) => {
      const prev = entries;
      setEntries((cur) => cur.map((e) => (e.id === entryId ? { ...e, status } : e)));
      const res = await clubRadarService.setStatus(entryId, status);
      if (res.success) {
        // Adopt the server's attribution stamp so the row shows "by <me>" at once.
        const d = res.data || {};
        setEntries((cur) =>
          cur.map((e) =>
            e.id === entryId
              ? {
                  ...e,
                  status,
                  status_set_by_name: d.status_set_by_name ?? e.status_set_by_name,
                  status_set_by_role: d.status_set_by_role ?? e.status_set_by_role,
                  status_set_at: d.status_set_at ?? e.status_set_at,
                }
              : e,
          ),
        );
      } else {
        setEntries(prev);
      }
      return res;
    },
    [entries],
  );

  // ── Label ops on an entry (optimistic on the entries array) ──────────────────
  const patchEntry = useCallback((entryId, patch) => {
    setEntries((cur) => cur.map((e) => (e.id === entryId ? { ...e, ...patch(e) } : e)));
  }, []);

  const assignLabel = useCallback(
    async (entryId, label) => {
      patchEntry(entryId, (e) => ({ labels: [...(e.labels || []), label] }));
      const res = await clubRadarService.assignLabel(entryId, label.id);
      if (!res.success) patchEntry(entryId, (e) => ({ labels: (e.labels || []).filter((l) => l.id !== label.id) }));
      return res;
    },
    [patchEntry],
  );

  const unassignLabel = useCallback(
    async (entryId, label) => {
      patchEntry(entryId, (e) => ({ labels: (e.labels || []).filter((l) => l.id !== label.id) }));
      const res = await clubRadarService.unassignLabel(entryId, label.id);
      if (!res.success) patchEntry(entryId, (e) => ({ labels: [...(e.labels || []), label] }));
      return res;
    },
    [patchEntry],
  );

  const createLabel = useCallback(async ({ name, color }) => {
    const res = await clubRadarService.createLabel({ name, color });
    if (res.success) setLabels((prev) => [...prev, res.data]);
    return res;
  }, []);

  // Add a note from the detail panel; bumps the card's latest-note preview.
  const addNote = useCallback(async (entryId, text, author) => {
    const res = await clubRadarService.addNote(entryId, text);
    if (res.success) {
      setEntries((cur) =>
        cur.map((e) =>
          e.id === entryId
            ? {
                ...e,
                latest_note: {
                  text,
                  author_name: res.data?.author_name || author?.name || "",
                  author_role: res.data?.author_role || author?.role || "",
                },
              }
            : e,
        ),
      );
    }
    return res;
  }, []);

  return {
    myLists,
    sharedLists,
    allLists,
    labels,
    entries,
    visibleEntries,
    statusCounts,
    selectedList,
    setSelectedList,
    loading,
    listsLoading,
    error,
    filter,
    setFilter,
    filterLabel,
    setFilterLabel,
    sortBy,
    setSortBy,
    fetchEntries,
    fetchLists,
    createList,
    updateList,
    deleteList,
    setSharing,
    removeEntry,
    moveEntry,
    copyEntry,
    setStatus,
    assignLabel,
    unassignLabel,
    createLabel,
    addNote,
  };
};

export default useClubRadar;
